<template>
    <v-form ref="form" @submit.prevent="submitForm">
        <v-tabs v-model="activeTab" color="primary" background-color="grey lighten-4">
            <v-tab href="#sales">{{ $t("invoicing.options.tabs.sales") }}</v-tab>
            <v-tab href="#purchase">{{ $t("invoicing.options.tabs.purchase") }}</v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab">
            <!-- Sales invoice options: grouped by expansion panels -->
            <v-tab-item value="sales">
                <v-expansion-panels v-model="openSalesPanels" multiple class="pa-2">
                    <template v-for="group in salesGroups">
                        <v-expansion-panel v-if="group.options.length > 0" :key="group.key">
                            <v-expansion-panel-header class="font-weight-medium">
                                {{ group.label }}
                                <template v-slot:actions>
                                    <v-chip x-small class="ml-2" color="grey lighten-2">{{ group.options.length }}</v-chip>
                                    <v-icon>mdi-chevron-down</v-icon>
                                </template>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                                <v-row
                                    v-for="opt in group.options"
                                    :key="opt._idx"
                                    class="mx-0 align-center"
                                    dense
                                >
                                    <v-col cols="auto" class="py-1">
                                        <v-checkbox v-model="checkedValues[opt._idx]" dense hide-details class="mt-0 pt-0" />
                                    </v-col>
                                    <v-col class="py-1">
                                        <template v-if="getOptionTypeLabel(opt.type) === 'String'">
                                            <v-text-field v-model="opt.value" :label="opt.name" :disabled="!opt.canBeModified" dense hide-details />
                                        </template>
                                        <template v-else-if="getOptionTypeLabel(opt.type) === 'Integer'">
                                            <v-text-field v-model="opt.value" :label="opt.name" type="number" :disabled="!opt.canBeModified" dense hide-details />
                                        </template>
                                        <template v-else-if="getOptionTypeLabel(opt.type) === 'Decimal'">
                                            <v-text-field v-model="decimalValues[opt._idx]" :label="opt.name" type="number" step="0.01" :disabled="!opt.canBeModified" dense hide-details />
                                        </template>
                                        <template v-else-if="getOptionTypeLabel(opt.type) === 'Date'">
                                            <div class="caption grey--text text--darken-1 mt-1">{{ opt.name }}</div>
                                            <v-date-picker v-model="opt.value" :disabled="!opt.canBeModified" />
                                        </template>
                                        <template v-else-if="getOptionTypeLabel(opt.type) === 'Boolean'">
                                            <v-switch v-model="booleanValues[opt._idx]" :label="opt.name" :disabled="!opt.canBeModified" dense hide-details />
                                        </template>
                                        <template v-else-if="getOptionTypeLabel(opt.type) === 'Guid'">
                                            <v-text-field v-model="opt.value" :label="opt.name" :disabled="!opt.canBeModified" dense hide-details />
                                        </template>
                                        <template v-else-if="getOptionTypeLabel(opt.type).startsWith('Dictionary')">
                                            <div class="caption grey--text text--darken-1 mt-1">{{ opt.name }}</div>
                                            <v-row v-for="(item, index) in opt.items" :key="index" dense>
                                                <v-col>
                                                    <v-text-field v-model="item.value" :disabled="!opt.canBeModified" dense hide-details>
                                                        <template v-slot:prepend>
                                                            <v-chip small class="mt-n1">{{ formatPercentage(item.key) }}</v-chip>
                                                        </template>
                                                    </v-text-field>
                                                </v-col>
                                            </v-row>
                                        </template>
                                    </v-col>
                                </v-row>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </template>
                </v-expansion-panels>
            </v-tab-item>

            <!-- Purchase invoice options: grouped by expansion panels -->
            <v-tab-item value="purchase">
                <v-expansion-panels v-model="openPurchasePanels" multiple class="pa-2">
                    <template v-for="group in purchaseGroups">
                        <v-expansion-panel v-if="group.options.length > 0" :key="group.key">
                            <v-expansion-panel-header class="font-weight-medium">
                                {{ group.label }}
                                <template v-slot:actions>
                                    <v-chip x-small class="ml-2" color="grey lighten-2">{{ group.options.length }}</v-chip>
                                    <v-icon>mdi-chevron-down</v-icon>
                                </template>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                                <v-row
                                    v-for="opt in group.options"
                                    :key="opt._idx"
                                    class="mx-0 align-center"
                                    dense
                                >
                                    <v-col cols="auto" class="py-1">
                                        <v-checkbox v-model="checkedValues[opt._idx]" dense hide-details class="mt-0 pt-0" />
                                    </v-col>
                                    <v-col class="py-1">
                                        <template v-if="getOptionTypeLabel(opt.type) === 'String'">
                                            <v-text-field v-model="opt.value" :label="opt.name" :disabled="!opt.canBeModified" dense hide-details />
                                        </template>
                                        <template v-else-if="getOptionTypeLabel(opt.type) === 'Integer'">
                                            <v-text-field v-model="opt.value" :label="opt.name" type="number" :disabled="!opt.canBeModified" dense hide-details />
                                        </template>
                                        <template v-else-if="getOptionTypeLabel(opt.type) === 'Decimal'">
                                            <v-text-field v-model="decimalValues[opt._idx]" :label="opt.name" type="number" step="0.01" :disabled="!opt.canBeModified" dense hide-details />
                                        </template>
                                        <template v-else-if="getOptionTypeLabel(opt.type) === 'Date'">
                                            <div class="caption grey--text text--darken-1 mt-1">{{ opt.name }}</div>
                                            <v-date-picker v-model="opt.value" :disabled="!opt.canBeModified" />
                                        </template>
                                        <template v-else-if="getOptionTypeLabel(opt.type) === 'Boolean'">
                                            <v-switch v-model="booleanValues[opt._idx]" :label="opt.name" :disabled="!opt.canBeModified" dense hide-details />
                                        </template>
                                        <template v-else-if="getOptionTypeLabel(opt.type) === 'Guid'">
                                            <v-text-field v-model="opt.value" :label="opt.name" :disabled="!opt.canBeModified" dense hide-details />
                                        </template>
                                        <template v-else-if="getOptionTypeLabel(opt.type).startsWith('Dictionary')">
                                            <div class="caption grey--text text--darken-1 mt-1">{{ opt.name }}</div>
                                            <v-row v-for="(item, index) in opt.items" :key="index" dense>
                                                <v-col>
                                                    <v-text-field v-model="item.value" :disabled="!opt.canBeModified" dense hide-details>
                                                        <template v-slot:prepend>
                                                            <v-chip small class="mt-n1">{{ formatPercentage(item.key) }}</v-chip>
                                                        </template>
                                                    </v-text-field>
                                                </v-col>
                                            </v-row>
                                        </template>
                                    </v-col>
                                </v-row>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </template>
                </v-expansion-panels>
            </v-tab-item>
        </v-tabs-items>
    </v-form>
</template>

<script>
import { InvoiceOptionsTypeMixin } from "../../models/InvoiceOptionsType";

const SALES_GROUPS = [
    { key: "salesGeneral", i18nKey: "invoicing.options.groups.salesGeneral", ranges: [[10000, 19999]] },
    { key: "emceSalesDetails", i18nKey: "invoicing.options.groups.invoiceDetails", ranges: [[210001, 210013], [210039, 210040]] },
    { key: "emceSalesTransportEvent", i18nKey: "invoicing.options.groups.transportEventRow", ranges: [[210014, 210018]] },
    { key: "emceSalesDeliveryParty", i18nKey: "invoicing.options.groups.deliveryParty", ranges: [[210019, 210025]] },
    { key: "emceSalesVatRow", i18nKey: "invoicing.options.groups.vatRow", ranges: [[210026, 210030], [210041, 210041]] },
    { key: "emceSalesDeductibleRow", i18nKey: "invoicing.options.groups.deductibleRow", ranges: [[210031, 210038]] },
    { key: "bcSales", i18nKey: "invoicing.options.groups.bcSales", ranges: [[110000, 119999]] }
];

const PURCHASE_GROUPS = [
    { key: "purchaseGeneral", i18nKey: "invoicing.options.groups.purchaseGeneral", ranges: [[20000, 29999]] },
    { key: "emcePurchaseGeneral", i18nKey: "invoicing.options.groups.generalSettings", ranges: [[220001, 220006]] },
    { key: "emcePurchaseTrips", i18nKey: "invoicing.options.groups.tripsRow", ranges: [[220007, 220018]] },
    { key: "emcePurchaseTripsVat", i18nKey: "invoicing.options.groups.tripsVatRow", ranges: [[220019, 220030]] },
    { key: "emcePurchaseTransaction", i18nKey: "invoicing.options.groups.transactionRow", ranges: [[220031, 220043]] },
    { key: "emcePurchaseTransactionVat", i18nKey: "invoicing.options.groups.transactionVatRow", ranges: [[220044, 220056]] },
    { key: "emcePurchaseExternalProvision", i18nKey: "invoicing.options.groups.externalProvisionRow", ranges: [[220057, 220069]] },
    { key: "emcePurchaseExternalProvisionVat", i18nKey: "invoicing.options.groups.externalProvisionVatRow", ranges: [[220070, 220082]] },
    { key: "emcePurchaseSettlement", i18nKey: "invoicing.options.groups.settlementRow", ranges: [[220083, 220095]] },
    { key: "emcePurchaseInstallments", i18nKey: "invoicing.options.groups.installments", ranges: [[220096, 220097]] },
    { key: "emcePurchaseVendorProvision", i18nKey: "invoicing.options.groups.vendorProvision", ranges: [[220098, 220113]] },
    { key: "emcePurchaseVendorProvisionVat", i18nKey: "invoicing.options.groups.vendorProvisionVatRow", ranges: [[220114, 220126]] },
    { key: "emcePurchaseInvoiceDates", i18nKey: "invoicing.options.groups.invoiceDates", ranges: [[220127, 220129]] },
    { key: "bcPurchase", i18nKey: "invoicing.options.groups.bcPurchase", ranges: [[120000, 129999]] }
];

function inRanges(optionId, ranges) {
    return ranges.some(([min, max]) => optionId >= min && optionId <= max);
}

export default {
    mixins: [InvoiceOptionsTypeMixin],
    props: {
        invoiceOptions: {
            type: Object,
            required: true,
            default: () => ({ invoicingOptions: [] })
        }
    },
    data() {
        return {
            booleanValues: {},
            checkedValues: {},
            decimalValues: {},
            activeTab: "sales",
            openSalesPanels: [],
            openPurchasePanels: []
        };
    },
    computed: {
        genericOptions() {
            return this.invoiceOptions.invoicingOptions.filter(opt =>
                (opt.optionId >= 1 && opt.optionId <= 9999) ||
                (opt.optionId >= 100001 && opt.optionId <= 109999) ||
                (opt.optionId >= 200001 && opt.optionId <= 209999)
            );
        },
        salesOptions() {
            return this.invoiceOptions.invoicingOptions.filter(opt =>
                (opt.optionId >= 10000 && opt.optionId <= 19999) ||
                (opt.optionId >= 110000 && opt.optionId <= 119999) ||
                (opt.optionId >= 210000 && opt.optionId <= 219999)
            );
        },
        purchaseOptions() {
            return this.invoiceOptions.invoicingOptions.filter(opt =>
                (opt.optionId >= 20000 && opt.optionId <= 29999) ||
                (opt.optionId >= 120000 && opt.optionId <= 129999) ||
                (opt.optionId >= 220000 && opt.optionId <= 229999)
            );
        },
        salesGroups() {
            return SALES_GROUPS.map(def => ({
                key: def.key,
                label: this.$t(def.i18nKey),
                options: this.salesOptions.filter(opt => inRanges(opt.optionId, def.ranges))
            }));
        },
        purchaseGroups() {
            return PURCHASE_GROUPS.map(def => ({
                key: def.key,
                label: this.$t(def.i18nKey),
                options: this.purchaseOptions.filter(opt => inRanges(opt.optionId, def.ranges))
            }));
        }
    },
    watch: {
        invoiceOptions: {
            handler() {
                this.initializeValues();
            },
            deep: true,
            immediate: true
        }
    },
    methods: {
        initializeValues() {
            this.booleanValues = {};
            this.checkedValues = {};
            this.decimalValues = {};
            this.invoiceOptions.invoicingOptions.forEach((opt, key) => {
                opt._idx = key;
                this.checkedValues[key] = opt.value !== null;
                if (this.getOptionTypeLabel(opt.type) === "Boolean") {
                    this.booleanValues[key] = this.fromBoolean(opt.value);
                } else if (this.getOptionTypeLabel(opt.type) === "Decimal") {
                    this.decimalValues[key] = this.fromDecimal(opt.value);
                } else if (this.getOptionTypeLabel(opt.type).startsWith("Dictionary")) {
                    opt.items = this.parseDictionary(opt.value, opt.type);
                }
            });
        },
        parseDictionary(value, type) {
            const parsed = JSON.parse(value);
            return Object.keys(parsed).map(key => {
                let itemValue = parsed[key];
                if (type === "DictionaryDecimal") {
                    itemValue = this.fromDecimal(itemValue);
                }
                return { key, value: itemValue };
            });
        },
        formatPercentage(value) {
            const formatter = new Intl.NumberFormat("fi-FI", {
                style: "percent",
                minimumFractionDigits: 0,
                maximumFractionDigits: 1
            });
            return formatter.format(value / 10000);
        },
        fromBoolean(value) {
            return value === "true" || value === true;
        },
        fromDecimal(value) {
            return (value / 100).toFixed(2);
        },
        toDecimal(value) {
            return Math.round(parseFloat(value) * 100);
        },
        toDictionary(items, type) {
            const dict = {};
            items.forEach(item => {
                let itemValue = item.value;
                if (type === "DictionaryDecimal") {
                    itemValue = this.toDecimal(itemValue);
                }
                dict[item.key] = itemValue;
            });
            return JSON.stringify(dict);
        },
        submitForm() {
            this.invoiceOptions.invoicingOptions.forEach((opt, key) => {
                if (!this.checkedValues[key]) {
                    opt.value = null;
                } else if (this.getOptionTypeLabel(opt.type) === "Boolean") {
                    opt.value = this.booleanValues[key];
                } else if (this.getOptionTypeLabel(opt.type) === "Decimal") {
                    opt.value = this.toDecimal(this.decimalValues[key]);
                } else if (this.getOptionTypeLabel(opt.type).startsWith("Dictionary")) {
                    opt.value = this.toDictionary(opt.items, opt.type);
                }
            });
            this.$emit("submit", this.invoiceOptions);
        }
    },
    mounted() {
        this.initializeValues();
    }
};
</script>
